/**
 * Organizations API — typed wrappers over the gateway endpoints in
 * apps/chaos-gateway/src/orgs.ts (Milestone 1.5a).
 *
 * Includes: org CRUD, memberships CRUD, constitution read/write + history.
 *
 * Wire shape from @chaos/types/orgs.
 */
import type {
  Constitution,
  ConstitutionHistory,
  CreateOrgInput,
  Organization,
  OrgMembership,
  OrgRole,
  OrgTeardownPreview,
  OrgTeardownRequestInput,
  OrgTeardownResult,
  ReorderOrgInput,
  UpdateConstitutionInput,
  UpdateMembershipInput,
  UpdateOrgInput,
} from '@chaos/types/orgs';

import { apiJson, apiUpload } from './client';

export interface MembershipWithUser extends OrgMembership {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    systemRole: string | null;
  } | null;
}

// ── Add-member (multi-user onboarding) invite contract ────────────────
// POST /api/orgs/:orgId/members { email, role } → 200 { member, oneTimePassword? }
// A founder is never minted via invite, so the role is restricted to the three
// lower tiers. `oneTimePassword` is present ONLY when a brand-NEW auth.users row
// was created (existing users just get attached) and must be shown to the
// inviter exactly once. The invite `member` deliberately omits systemRole (the
// gateway withholds the invited user's instance system_role from the inviter).
export type OrgInviteRole = 'admin' | 'member' | 'viewer';
export interface AddMemberResult {
  member: {
    orgId: string;
    userId: string;
    orgRole: OrgRole;
    createdAt: string;
    user: { id: string; email: string; displayName: string | null } | null;
  };
  oneTimePassword?: string;
  // 2026-08-01 — how many credential grants the first-member rule switched off.
  // 0 when the Workspace was already shared, when there was nothing placed by
  // "All Workspaces", or when the operator unticked the box in the dialog.
  credentialGrantsRevoked?: number;
  // Present only when the revoke succeeded but running agents could NOT be told
  // to rebuild: that many sessions are still holding the old bundle. Surfaced
  // rather than swallowed — "revoked" that did not reach the containers is the
  // one thing this response must not imply.
  credentialGrantsStaleSessions?: number;
  credentialGrantsError?: string;
}

// GET /api/credentials/workspace-grants?orgId= — what adding a person would
// cost this Workspace. Counts only; no credential is named.
export interface WorkspaceGrantSummary {
  orgId: string;
  memberCount: number;
  /** Still one person. The first-member rule only fires here. */
  solo: boolean;
  /** Ticks placed by "All Workspaces" — the ones the rule would clear. */
  viaAllWorkspaces: number;
  /** Ticks placed by hand on this Workspace — never cleared by the rule. */
  manual: number;
}

interface OrgsListResponse {
  orgs: Organization[];
}
interface OrgResponse {
  org: Organization;
}
interface MembersListResponse {
  members: MembershipWithUser[];
}
interface MembershipResponse {
  membership: OrgMembership;
}
interface ConstitutionResponse {
  constitution: Constitution | null;
}
interface ConstitutionHistoryListResponse {
  history: ConstitutionHistory[];
}
interface ConstitutionHistoryItemResponse {
  history: ConstitutionHistory;
}

// ── Instruction ladder tree (VITE_INSTRUCTIONS_V2) ────────────────────
// Read-only aggregator shape returned by GET /api/orgs/:orgId/instruction-tree.
// Mirrors apps/chaos-gateway/src/orgs.ts. Layer numbers mirror the spawn-time
// instruction-cascade ordering in profile-bundle.ts (org=01, project=05,
// board=06, status/column=07) so the UI can show precedence to the founder.
export interface InstructionTreeStatusNode {
  columnId: string;
  name: string;
  kind: string | null;
  instructions: string | null;
  layerNo: number;
}
export interface InstructionTreeBoardNode {
  id: string;
  name: string;
  instructions: string | null;
  layerNo: number;
  statuses: InstructionTreeStatusNode[];
}
export interface InstructionTreeProjectNode {
  id: string;
  name: string;
  instructions: string | null;
  layerNo: number;
  boards: InstructionTreeBoardNode[];
}
export interface InstructionTreeOrgNode {
  // Per-org constitution body (chaos.constitutions) — the editable org layer.
  constitutionBody: string | null;
  currentVersion: number | null;
  // Account base body inherited by every org (chaos.constitution_base). null
  // when the all-orgs base read is unavailable (ALL_ORGS_SETTINGS_ENABLED OFF).
  constitutionBase: string | null;
  layerNo: number;
}
export interface InstructionTree {
  org: InstructionTreeOrgNode;
  projects: InstructionTreeProjectNode[];
}

export const orgsApi = {
  // ── Organizations ─────────────────────────────────────────────────
  list(): Promise<Organization[]> {
    return apiJson<OrgsListResponse>('/orgs').then((r) => r.orgs);
  },
  get(orgId: string): Promise<Organization> {
    return apiJson<OrgResponse>(`/orgs/${orgId}`).then((r) => r.org);
  },
  create(input: CreateOrgInput): Promise<Organization> {
    return apiJson<OrgResponse>('/orgs', { method: 'POST', json: input }).then((r) => r.org);
  },
  update(orgId: string, input: UpdateOrgInput): Promise<Organization> {
    return apiJson<OrgResponse>(`/orgs/${orgId}`, { method: 'PATCH', json: input }).then(
      (r) => r.org,
    );
  },
  archive(orgId: string): Promise<Organization> {
    return apiJson<OrgResponse>(`/orgs/${orgId}`, { method: 'DELETE' }).then((r) => r.org);
  },

  // ── Settings V2 (0064) — drag-to-reorder + per-org logo ────────────
  /** Reorder companies within the caller's owned account (1-based positions). */
  reorder(input: ReorderOrgInput): Promise<Organization[]> {
    return apiJson<OrgsListResponse>('/orgs/reorder', {
      method: 'POST',
      json: input,
    }).then((r) => r.orgs);
  },
  /**
   * Upload/replace the per-org logo (multipart). Returns the updated org.
   * Retries a transient reset / 5xx instead of failing on the first blip.
   *
   * Same shape as `authApi.uploadAvatar` and the same verdict: it needs the
   * verified pre-send read and the fail-fast on the gateway's 413 (1 MB) and
   * 415 (png/jpeg/webp/gif/svg), both of which now come from `apiUpload`. It
   * does NOT need chunked resume or a local copy — the logo is a file the
   * uploader still has. `uploadM.onError` puts the error's own message in the
   * toast, so a refusal reaches the person who can act on it.
   */
  uploadLogo(orgId: string, file: File): Promise<Organization> {
    return apiUpload<OrgResponse>(`/orgs/${orgId}/logo`, file, {
      filename: file.name,
    }).then((r) => r.org);
  },
  /** Remove the per-org logo (falls back to the auto identity). */
  removeLogo(orgId: string): Promise<Organization> {
    return apiJson<OrgResponse>(`/orgs/${orgId}/logo`, { method: 'DELETE' }).then((r) => r.org);
  },

  // ── Memberships ───────────────────────────────────────────────────
  listMembers(orgId: string): Promise<MembershipWithUser[]> {
    return apiJson<MembersListResponse>(`/orgs/${orgId}/members`).then((r) => r.members);
  },
  /**
   * Invite/attach a member by EMAIL (multi-user onboarding). Existing users are
   * attached; a brand-new user is created and the response carries a one-time
   * password to hand off. 403 if the caller is not founder/admin of the org;
   * 409 if the email is already a member.
   */
  addMember(
    orgId: string,
    input: {
      email: string;
      role: OrgInviteRole;
      /**
       * The founder's rule, 2026-08-01: the first other person in a Workspace
       * switches off the credential ticks that "All Workspaces" put there.
       * DEFAULTS TO TRUE server-side — pass false to keep them. Hand-placed
       * ticks are never affected either way.
       */
      revokeAllWorkspaceCredentialGrants?: boolean;
      /**
       * 0232 — the owner has SEEN what connecting this person switches off,
       * across all seven surfaces, and said yes. Absent or false while
       * something would be revoked ⇒ the gateway refuses with 409
       * `revocation_not_acknowledged` and hands the impact back in the body.
       */
      acknowledgeRevocation?: boolean;
    },
  ): Promise<AddMemberResult> {
    return apiJson<AddMemberResult>(`/orgs/${orgId}/members`, {
      method: 'POST',
      json: input,
    });
  },
  /**
   * What would adding a person to this Workspace cost? Read by the add-member
   * dialog so the checkbox is offered only when it would actually do something.
   * Founder/admin of the Workspace only (403 otherwise).
   */
  workspaceGrantSummary(orgId: string): Promise<WorkspaceGrantSummary> {
    return apiJson<WorkspaceGrantSummary>(
      `/credentials/workspace-grants?orgId=${encodeURIComponent(orgId)}`,
    );
  },
  updateMember(
    orgId: string,
    userId: string,
    input: UpdateMembershipInput,
  ): Promise<OrgMembership> {
    return apiJson<MembershipResponse>(`/orgs/${orgId}/members/${userId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.membership);
  },
  removeMember(orgId: string, userId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/orgs/${orgId}/members/${userId}`, {
      method: 'DELETE',
    });
  },
  /**
   * Get the calling user's membership for an org. Returns null when the
   * user has no row (and is not a system founder). System founders get a
   * synthetic { orgRole: 'founder', viaSystemFounder: true } row.
   */
  getMyMembership(
    orgId: string,
  ): Promise<{ membership: OrgMembership | null; viaSystemFounder: boolean }> {
    return apiJson<{
      membership: OrgMembership | null;
      viaSystemFounder: boolean;
    }>(`/orgs/${orgId}/members/me`);
  },

  // ── Constitution ──────────────────────────────────────────────────
  getConstitution(orgId: string): Promise<Constitution | null> {
    return apiJson<ConstitutionResponse>(`/orgs/${orgId}/constitution`).then(
      (r) => r.constitution,
    );
  },
  updateConstitution(orgId: string, input: UpdateConstitutionInput): Promise<Constitution> {
    return apiJson<{ constitution: Constitution }>(`/orgs/${orgId}/constitution`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.constitution);
  },
  listConstitutionHistory(orgId: string): Promise<ConstitutionHistory[]> {
    return apiJson<ConstitutionHistoryListResponse>(
      `/orgs/${orgId}/constitution/history`,
    ).then((r) => r.history);
  },
  getConstitutionHistory(orgId: string, version: number): Promise<ConstitutionHistory> {
    return apiJson<ConstitutionHistoryItemResponse>(
      `/orgs/${orgId}/constitution/history/${version}`,
    ).then((r) => r.history);
  },

  // ── Instruction ladder tree (VITE_INSTRUCTIONS_V2) ──────────────────
  // One-shot read aggregator: org constitution (body + base) + every
  // project → board → status with its instructions snippet, so the tree
  // page does not N+1. Org-member read; additive; inert.
  getInstructionTree(orgId: string): Promise<InstructionTree> {
    return apiJson<InstructionTree>(`/orgs/${orgId}/instruction-tree`);
  },
};

// ── Query-key factory ───────────────────────────────────────────────
export const orgsKeys = {
  all: ['orgs'] as const,
  list: () => [...orgsKeys.all, 'list'] as const,
  detail: (orgId: string) => [...orgsKeys.all, 'detail', orgId] as const,
  members: (orgId: string) => [...orgsKeys.all, 'members', orgId] as const,
  myMembership: (orgId: string) => [...orgsKeys.all, 'my-membership', orgId] as const,
  constitution: (orgId: string) => [...orgsKeys.all, 'constitution', orgId] as const,
  constitutionHistory: (orgId: string) =>
    [...orgsKeys.all, 'constitution', orgId, 'history'] as const,
  instructionTree: (orgId: string) =>
    [...orgsKeys.all, 'instruction-tree', orgId] as const,
  workspaceGrants: (orgId: string) =>
    [...orgsKeys.all, 'workspace-grants', orgId] as const,
};

// ── Guided Org Teardown (Org Architecture Phase E) ──────────────────
// Owner-gated "Delete organization". Mirrors apps/chaos-gateway/src/org-teardown.ts.
// All routes 404 when ORG_TEARDOWN_ENABLED is OFF (default).
export const orgTeardownApi = {
  /** Per-entity-type counts + dependency disposition for the teardown screen. */
  preview(orgId: string): Promise<OrgTeardownPreview> {
    return apiJson<OrgTeardownPreview>(`/account/orgs/${orgId}/teardown-preview`);
  },
  /** Run the guided teardown with per-type keep|delete choices + slug confirm. */
  teardown(orgId: string, input: OrgTeardownRequestInput): Promise<OrgTeardownResult> {
    return apiJson<OrgTeardownResult>(`/account/orgs/${orgId}/teardown`, {
      method: 'POST',
      json: input,
    });
  },
};

export const orgTeardownKeys = {
  all: ['org-teardown'] as const,
  preview: (orgId: string) => [...orgTeardownKeys.all, 'preview', orgId] as const,
};

export type { OrgRole };
